import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid,
} from 'recharts';
import { useTheme } from '../theme';
import '../styles/AdminCharts.css';

const FREQUENCIES_ORDER = ['One-time', 'Daily', 'Weekly', 'Monthly'];

function getWinsByMonth(wins) {
  const counts = {};
  wins.forEach((w) => {
    if (!w.created) return;
    const d = new Date(w.created);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    counts[key] = (counts[key] || 0) + 1;
  });
  return Object.entries(counts)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-12)
    .map(([month, count]) => ({
      month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
      count,
    }));
}

function getWinsByDept(wins) {
  const counts = {};
  wins.forEach((w) => {
    const dept = w.department || 'Unknown';
    counts[dept] = (counts[dept] || 0) + 1;
  });
  return Object.entries(counts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 12)
    .map(([dept, count]) => ({ dept, count }));
}

function getCapacityByDept(wins) {
  const hours = {};
  wins.forEach((w) => {
    if (!w.timeSavedMinutes) return;
    const dept = w.department || 'Unknown';
    hours[dept] = (hours[dept] || 0) + w.timeSavedMinutes;
  });
  return Object.entries(hours)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 12)
    .map(([dept, mins]) => ({ dept, hours: parseFloat((mins / 60).toFixed(1)) }));
}

function getCapacityByFrequency(wins) {
  const totals = {};
  FREQUENCIES_ORDER.forEach((f) => { totals[f] = 0; });
  wins.forEach((w) => {
    if (!w.timeSavedMinutes || !w.frequency) return;
    if (totals[w.frequency] !== undefined) {
      totals[w.frequency] += w.timeSavedMinutes;
    }
  });
  return FREQUENCIES_ORDER
    .filter((f) => totals[f] > 0)
    .map((f) => ({ freq: f, hours: parseFloat((totals[f] / 60).toFixed(1)) }));
}

const PINK  = '#e05a7a';
const AMBER = '#f57c00';

const CustomTooltip = ({ active, payload, label, suffix = '' }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip-label">{label}</p>
      <p className="chart-tooltip-value">{payload[0].value}{suffix}</p>
    </div>
  );
};

function getMostLiked(wins, likeCounts, limit = 10) {
  return wins
    .map((w) => ({ ...w, likes: likeCounts[String(w.id)] || 0 }))
    .filter((w) => w.likes > 0)
    .sort((a, b) => b.likes - a.likes)
    .slice(0, limit)
    .map((w) => ({
      win: w,
      label: w.task.length > 40 ? w.task.slice(0, 40) + '…' : w.task,
      likes: w.likes,
    }));
}

const ClickableBar = (props) => {
  const { x, y, width, height, win, onSelectWin } = props;
  return (
    <rect
      x={x} y={y} width={Math.max(width, 0)} height={height}
      fill={PINK} rx={4} ry={4}
      style={{ cursor: 'pointer' }}
      onClick={() => onSelectWin && onSelectWin(win)}
    />
  );
};

const ClickableFreqBar = (props) => {
  const { x, y, width, height, freq, onFreqClick, activeFreq } = props;
  const isActive = activeFreq instanceof Set ? activeFreq.has(freq) : false;
  const anyActive = activeFreq instanceof Set ? activeFreq.size > 0 : false;
  return (
    <rect
      x={x} y={y} width={Math.max(width, 0)} height={height}
      fill={isActive ? '#e65100' : AMBER} rx={[4, 4, 0, 0]}
      style={{
        cursor: 'pointer',
        opacity: anyActive && !isActive ? 0.4 : 1,
        transition: 'opacity 0.15s',
      }}
      onClick={() => onFreqClick && onFreqClick(freq)}
    />
  );
};

export default function AdminCharts({ wins, likeCounts = {}, onSelectWin, timeRange, onTimeRangeChange, onFreqClick, activeFreq }) {
  const { effective } = useTheme();
  const isDark = effective === 'dark';
  // Recharts colors are set in JS, so derive grid/axis/data colors from the theme.
  const BLUE = isDark ? '#4d9be6' : '#005eb8';
  const GREEN = isDark ? '#52b46a' : '#2e7d32';
  const gridStroke = isDark ? '#2c3850' : '#f0f0f0';
  const axisTick = isDark ? '#9fb0c7' : '#666666';

  const byMonth = getWinsByMonth(wins);
  const byDept = getWinsByDept(wins);
  const capacityByDept = getCapacityByDept(wins);
  const capacityByFreq = getCapacityByFrequency(wins);
  const mostLiked = getMostLiked(wins, likeCounts);

  const totalHours = (wins.reduce((s, w) => s + (w.timeSavedMinutes || 0), 0) / 60).toFixed(1);
  const totalWins = wins.length;
  const topDept = byDept[0]?.dept || '—';

  return (
    <div className="admin-charts">
      <div className="admin-charts-header">
        <div className="admin-charts-stats">
          <div className="admin-charts-stat">
            <span className="admin-charts-stat-value">{totalWins}</span>
            <span className="admin-charts-stat-label">Total Wins</span>
          </div>
          <div className="admin-charts-stat">
            <span className="admin-charts-stat-value">{totalHours}h</span>
            <span className="admin-charts-stat-label">Total Capacity Added</span>
          </div>
          <div className="admin-charts-stat">
            <span className="admin-charts-stat-value">{topDept}</span>
            <span className="admin-charts-stat-label">Most Active Department</span>
          </div>
        </div>
        <div className="chart-time-toggle">
          <button
            className={`chart-time-btn ${timeRange === 'all' ? 'active' : ''}`}
            onClick={() => onTimeRangeChange('all')}
          >
            All Time
          </button>
          <button
            className={`chart-time-btn ${timeRange === 'week' ? 'active' : ''}`}
            onClick={() => onTimeRangeChange('week')}
          >
            This Week
          </button>
        </div>
      </div>

      <div className="admin-charts-grid">

        <div className="chart-card">
          <h4 className="chart-title">Wins Submitted Over Time</h4>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={byMonth} margin={{ top: 8, right: 16, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: axisTick }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: axisTick }} />
              <Tooltip content={<CustomTooltip suffix=" wins" />} />
              <Line type="monotone" dataKey="count" stroke={BLUE} strokeWidth={2.5} dot={{ r: 4, fill: BLUE }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h4 className="chart-title">Wins by Department</h4>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={byDept} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: axisTick }} />
              <YAxis type="category" dataKey="dept" tick={{ fontSize: 11, fill: axisTick }} width={130} />
              <Tooltip content={<CustomTooltip suffix=" wins" />} />
              <Bar dataKey="count" fill={GREEN} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {mostLiked.length > 0 && (
          <div className="chart-card chart-card--wide">
            <h4 className="chart-title">♥ Most Liked Wins <span className="chart-title-hint">— click a bar to view</span></h4>
            <ResponsiveContainer width="100%" height={Math.max(180, mostLiked.length * 40)}>
              <BarChart
                data={mostLiked}
                layout="vertical"
                margin={{ top: 4, right: 40, left: 8, bottom: 0 }}
              >
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: axisTick }} />
                <YAxis type="category" dataKey="label" tick={{ fontSize: 11, fill: axisTick }} width={200} />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <div className="chart-tooltip">
                        <p className="chart-tooltip-label">{d.win.task}</p>
                        <p className="chart-tooltip-value">♥ {d.likes}</p>
                        {d.win.department && <p className="chart-tooltip-dept">{d.win.department}</p>}
                      </div>
                    );
                  }}
                />
                <Bar dataKey="likes" shape={(props) => <ClickableBar {...props} win={props.win} onSelectWin={onSelectWin} />} label={{ position: 'right', fontSize: 11, fontWeight: 700, fill: '#e05a7a' }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {capacityByDept.length > 0 && (
          <div className="chart-card chart-card--wide">
            <h4 className="chart-title">Hours Added to Capacity by Department</h4>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={capacityByDept} margin={{ top: 8, right: 16, left: -10, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                <XAxis dataKey="dept" tick={{ fontSize: 11, fill: axisTick }} angle={-35} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 11, fill: axisTick }} />
                <Tooltip content={<CustomTooltip suffix="h" />} />
                <Bar dataKey="hours" fill={PINK} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {capacityByFreq.length > 0 && (
          <div className="chart-card chart-card--wide">
            <h4 className="chart-title">
              Hours Added to Capacity by Frequency
              <span className="chart-title-hint"> — click a bar to filter the list below</span>
            </h4>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={capacityByFreq} margin={{ top: 8, right: 24, left: -10, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                <XAxis dataKey="freq" tick={{ fontSize: 12, fill: axisTick }} />
                <YAxis tick={{ fontSize: 11, fill: axisTick }} />
                <Tooltip content={<CustomTooltip suffix="h" />} />
                <Bar
                  dataKey="hours"
                  shape={(props) => (
                    <ClickableFreqBar
                      {...props}
                      freq={props.freq}
                      onFreqClick={onFreqClick}
                      activeFreq={activeFreq}
                    />
                  )}
                  label={{ position: 'top', fontSize: 11, fontWeight: 700, fill: AMBER }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

      </div>
    </div>
  );
}
